/** @module                             React */

import { useEffect, useState }          from "react";

/** @module                             i18n */

import { useTranslation }               from "react-i18next";

/** @module                             Hooks */

import { usePagename }                  from "../../Hooks/usePagename";

import useFetch                         from "../../Hooks/useFetch";

import useUpdate                        from "../../Hooks/Mutations/useUpdate";

/** @module                             Queries */

import { FETCH_DELIVERY_PLAN_SETTINGS } from "../../Queries/DeliveryPlans";

/** @module                             Mutations */

import { 
    
    UPDATE_DELIVERY_PLAN, 
    
    SYNC_DELIVERY_PLANS }               from "../../Mutations/DeliveryPlans";

/** @module                             React-Icons */

import { 
    
    MdOutlineUpdate, 
    
    MdRestore, 
    
    MdCheck,
    
    MdSync }                            from "react-icons/md";

/** @module                             Components */

import Breadcrumbs                      from "../../Components/Dashboard/Breadcrumbs";

import Fieldset                         from "../../Components/Dashboard/Global/Forms/Fieldset";

import ComponentLoader                  from "../../Components/Dashboard/Loaders/ComponentLoader";

import ControlledCheckbox               from "../../Components/Dashboard/Global/Forms/ControlledCheckbox";

import { LabeledInput }                 from "../../Components/Dashboard/Global/Forms/Inputs_Spread";

import PricingFields                    from "../../Components/Dashboard/Shop/Delivery/Pricing_Fields";

import PriceFields, { 
    
    WHOLE_NUMBER_FIELDS }               from "../../Components/Dashboard/Shop/Delivery/Price_Fields";

/** @module                             Helpers */

import { 
    
    setIn, 
    
    coercePricing, 
    
    camelize, 
    
    toNumberOrNull }                    from "../../Components/Dashboard/Shop/Delivery/Pricing_Utils";

const 

/** the price settings that have their own column, always editable whatever the provider */

MASTER_PRICE_FIELDS = [ "master_price" ],

/**
 * 
 * @param { Object } plan
 * 
 * @returns the form state for one delivery method
 * 
 * @description The scalar prices are held as text while editing so a half-typed "5." survives
 *              a re-render; they are turned back into numbers on save.
 * 
 */

planToState = plan => ( {

    enabled: plan.enabled || false,

    visibleInB2c: plan.visibleInB2c || false,

    masterPriceEnabled: plan.masterPriceEnabled || false,

    prices: Object.fromEntries (

        ( plan.priceFields || [] ).map ( field => { const name = camelize ( field );

            return [ name, plan [ name ] === null || plan [ name ] === undefined ? "" : String ( plan [ name ] ) ];

        } )

    ),

    pricing: plan.pricing || {}

} ),

/**
 * 
 * @param { Array } fields
 * 
 * @param { Object } prices
 * 
 * @returns the scalar prices as mutation variables, blanks becoming null so the column clears
 * 
 */

priceVariables = ( fields = [], prices = {} ) => Object.fromEntries (

    [ ...new Set ( [ ...fields, ...MASTER_PRICE_FIELDS ] ) ]

        .filter ( field => field !== "master_price_enabled" )

        .map ( field => { const name = camelize ( field );

            return [ name, toNumberOrNull ( prices [ name ], WHOLE_NUMBER_FIELDS.includes ( field ) ) ];

        } )

),


/**
 * 
 * @param { String } name
 * 
 * @returns the delivery settings page, one card per delivery method
 * 
 */

Delivery = ( { name } ) => { usePagename ( name ); const { t } = useTranslation (),

    { loading, data, refetch } = useFetch ( FETCH_DELIVERY_PLAN_SETTINGS, {}, "cache-and-network" ),

    /** @callback useUpdate registers delivery methods that are in the code but not in the database */

    { updateMutation: syncMutation, loading: syncLoading } = useUpdate ( SYNC_DELIVERY_PLANS, {

        onCompleted: () => refetch ()

    } ),

    plans = ( data?.adminDeliveryPlanSettings || [] ).filter ( plan => {

        const fields = ( plan.priceFields || [] ).filter ( field => ! [ "master_price", "master_price_enabled" ].includes ( field ) );

        return fields.length > 0 || Object.keys ( plan.pricing || {} ).length > 0;

    } );

    /** @returns */

    return ( <section className="delivery">

        <Breadcrumbs />

        <div className="delivery__intro">

            <p>

                { t ( "delivery_pricing_intro" ) }

            </p>

            <button type="button" className="secondary" disabled={ syncLoading } onClick={ () => syncMutation ( {} ) }>

                <MdSync />

                <span>

                    { t ( "delivery_pricing_sync" ) }

                </span>

            </button>

        </div>

        { ( loading && ! plans.length ) ? <ComponentLoader /> : plans.map ( plan => 
        
            <DeliveryPlanCard key={ plan.id } plan={ plan } refetch={ refetch } /> 
            
        ) }

    </section> );

},

/**
 * 
 * @param { Object } plan
 * 
 * @param { Function } refetch
 * 
 * @returns the editable settings of a single delivery method
 * 
 */

DeliveryPlanCard = ( { plan, refetch } ) => { const { t } = useTranslation (),

    /** @event useState form fields */

    [ state, setState ] = useState ( () => planToState ( plan ) ),

    /** @callback useUpdate update mutation hook */

    { updateMutation, loading: updateLoading, complete: updateComplete } = useUpdate ( UPDATE_DELIVERY_PLAN, {

        onCompleted: () => refetch ()

    } ),

    /**
     * 
     * @param { Array } path
     * 
     * @param { * } value
     * 
     * @event handlePricingChange
     * 
     */

    handlePricingChange = ( path, value ) => {

        setState ( previous => ( { ...previous, pricing: setIn ( previous.pricing, path, value ) } ) );

    },

    /**
     * 
     * @param { String } name
     * 
     * @param { String } value
     * 
     * @event handlePriceChange for the settings backed by their own column
     * 
     */

    handlePriceChange = ( name, value ) => {

        setState ( previous => ( { ...previous, prices: { ...previous.prices, [ name ]: value } } ) );

    },

    /**
     * 
     * @param { Object } e
     * 
     * @event onSubmit
     * 
     * @returns update mutation
     * 
     * @description Sends the settings verbatim rather than as a patch, so a bracket removed
     *              here is also removed in the database.
     * 
     */

    onSubmit = e => { e.preventDefault ();

        updateMutation ( {

            id: plan.id,

            enabled: state.enabled,

            visibleInB2c: state.visibleInB2c,

            masterPriceEnabled: state.masterPriceEnabled,

            ...priceVariables ( plan.priceFields, state.prices ),

            pricing: coercePricing ( state.pricing ),

            replacePricing: true

        } );

    },

    /**
     * 
     * @event onReset
     * 
     * @returns update mutation restoring the provider defaults
     * 
     */

    onReset = () => {

        updateMutation ( { id: plan.id, resetPricing: true } );

    };

    /** @event useEffect keep the form in step with a refetched plan */

    useEffect ( () => { setState ( planToState ( plan ) ) }, [ plan ] );

    /** @returns */

    return ( <form className="content__vertical-spacer delivery-plan" onSubmit={ onSubmit }>

        <Fieldset 
        
            legend={ t ( plan.planId, plan.planId ) } 
            
            description={ t ( "delivery_pricing_plan_desc", { planId: plan.planId } ) }>

            <div className="delivery-plan__header">

                { plan.logoUrl && <img className="delivery-plan__logo" src={ plan.logoUrl } alt="" /> }

                <div className="delivery-plan__switches">

                    <ControlledCheckbox 
                    
                        id={ `${ plan.planId }-enabled` }

                        text={ t ( "enabled" ) }

                        state={ state.enabled }

                        disabled={ updateLoading }

                        action={ e => setState ( previous => ( { ...previous, enabled: e.target.checked } ) ) } />

                    <ControlledCheckbox 
                    
                        id={ `${ plan.planId }-visible-in-b2c` }

                        text={ t ( "delivery_pricing_visible_in_b2c" ) }

                        state={ state.visibleInB2c }

                        disabled={ updateLoading }

                        action={ e => setState ( previous => ( { ...previous, visibleInB2c: e.target.checked } ) ) } />

                </div>

            </div>

            <div className="pricing-group">

                <div className="pricing-group__title">

                    { t ( "delivery_pricing_master_price_enabled" ) }

                </div>

                <div className="pricing-grid pricing-grid--master">

                    <ControlledCheckbox 
                    
                        id={ `${ plan.planId }-master-price-enabled` }

                        text={ t ( "delivery_pricing_master_price_desc" ) }

                        state={ state.masterPriceEnabled }

                        disabled={ updateLoading }

                        action={ e => setState ( previous => ( { ...previous, masterPriceEnabled: e.target.checked } ) ) } />

                    { state.masterPriceEnabled && <LabeledInput 

                        type="text"

                        inputMode="decimal"

                        label="delivery_pricing_master_price"

                        value={ state.prices.masterPrice ?? "" }

                        disabled={ updateLoading }

                        action={ e => handlePriceChange ( "masterPrice", e.target.value ) } /> }

                </div>

            </div>

            { ! state.masterPriceEnabled && <PriceFields 
            
                fields={ plan.priceFields } 
                
                values={ state.prices } 
                
                onChange={ handlePriceChange } 
                
                disabled={ updateLoading } /> }

            { ! state.masterPriceEnabled && <PricingFields 
            
                value={ state.pricing } 
                
                onChange={ handlePricingChange } 
                
                disabled={ updateLoading } /> }

        </Fieldset>

        <div className="actions">

            <button disabled={ updateLoading } className={ updateComplete ? "is-saved" : undefined }>

                { updateComplete ? <MdCheck /> : <MdOutlineUpdate /> }

                <span>

                    { t ( updateComplete ? "delivery_pricing_saved" : "save" ) }

                </span>

            </button>

            <button type="button" className="secondary" disabled={ updateLoading } onClick={ onReset }>

                <MdRestore />

                <span>

                    { t ( "delivery_pricing_reset" ) }

                </span>

            </button>

        </div>

    </form> );

}

/** @exports Delivery */
 
export default Delivery;
